import { CssBaseline, LinearProgress } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import GlobalErrorBoundary from 'GlobalErrorBoundary';
import { Amplify } from 'aws-amplify';
import Layout from 'components/layout/Layout';
import LayoutSkeleton from 'components/layout/LayoutSkeleton';
import config from 'config';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { queryClient } from 'lib/react-query';
import { SnackbarProvider } from 'notistack';
import ApiUsagePage from 'pages/settings/api-usage';
import TagsPage from 'pages/settings/tags';
import DomainsPage from 'pages/settings/domains';
import queryString from 'query-string';
import type { FC } from 'react';
import React, { Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter, redirect } from 'react-router-dom';
import { Titled } from 'react-titled';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import AppsPage from './pages/settings/apps';
import CompetitorsContainer from './components/layout/CompetitorsContainer';
import { Login } from 'features/login';
import AuthRequired from 'lib/auth/AuthRequired';
import { Authenticator } from '@aws-amplify/ui-react';
import HotjarInitial from 'lib/hotjar/HotjarInitial';
import CreativesFbAdsPage from './pages/creatives-fb-ads';
import TrendsPage from './pages/trends';
import {
  getPageCompanyBranchRelationsOptions,
  getDomainFlowRelationsOptions,
  getAdvertiserCompanyDomainRelationsOptions,
} from 'features/ad-lib-creatives';

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: config.REGION,
    userPoolId: config.COGNITO_USER_POOL_ID,
    userPoolWebClientId: config.COGNITO_APP_CLIENT_ID,
    oauth: {
      domain: config.COGNITO_DOMAIN,
      scope: ['email', 'openid'],
      redirectSignIn: config.COGNITO_REDIRECT_SIGN_IN ?? `${window.location.origin}/`,
      redirectSignOut: config.COGNITO_REDIRECT_SIGN_OUT ?? `${window.location.origin}/`,
      responseType: 'code',
    },
  },
});

const Providers: FC = () => (
  <QueryParamProvider
    adapter={ReactRouter6Adapter}
    options={{
      searchStringToObject: (str) => queryString.parse(str, { arrayFormat: 'comma' }),
      objectToSearchString: (input) => queryString.stringify(input, { arrayFormat: 'comma' }),
    }}
  >
    <CssBaseline />
    <Authenticator.Provider>
      <QueryClientProvider client={queryClient}>
        <GlobalErrorBoundary>
          <SnackbarProvider
            autoHideDuration={1000}
            maxSnack={5}
            anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
            preventDuplicate
          >
            <HotjarInitial>
              <Suspense
                fallback={
                  <LayoutSkeleton>
                    <LinearProgress sx={{ mx: -3 }} />
                  </LayoutSkeleton>
                }
              >
                <Outlet />
              </Suspense>
            </HotjarInitial>
          </SnackbarProvider>
        </GlobalErrorBoundary>
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </QueryClientProvider>
    </Authenticator.Provider>
  </QueryParamProvider>
);

const router = createBrowserRouter([
  {
    element: (
      <Titled title="WellSpy">
        <Providers />
      </Titled>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/creatives-ads-lib" replace />,
      },
      {
        path: '/creatives-ads-lib',
        loader: ({ request }) => {
          const url = new URL(request.url);
          const network = url.searchParams.get('network') ?? undefined;
          const region = url.searchParams.get('region') ?? undefined;
          if (network === 'google') {
            void queryClient.prefetchQuery(getAdvertiserCompanyDomainRelationsOptions({ network, region }));
          } else {
            void queryClient.prefetchQuery(getPageCompanyBranchRelationsOptions({ network, region }));
            void queryClient.prefetchQuery(getDomainFlowRelationsOptions({ network, region }));
          }
          return null;
        },
        element: (
          <AuthRequired>
            <Layout>
              <CreativesFbAdsPage />
            </Layout>
          </AuthRequired>
        ),
      },
      {
        path: '/creatives-fb-ads-lib',
        loader: ({ request }) => {
          const url = new URL(request.url);
          return redirect(`/creatives-ads-lib${url.search}`);
        },
      },
      {
        path: '/trends',
        element: (
          <AuthRequired>
            <Layout>
              <TrendsPage />
            </Layout>
          </AuthRequired>
        ),
      },
      {
        path: '/settings',
        children: [
          {
            path: 'tags',
            element: (
              <AuthRequired>
                <Layout>
                  <TagsPage />
                </Layout>
              </AuthRequired>
            ),
          },
          {
            path: 'competitors',
            element: (
              <AuthRequired>
                <Layout>
                  <CompetitorsContainer />
                </Layout>
              </AuthRequired>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="/settings/competitors/domains" replace />,
              },
              {
                path: 'domains',
                element: (
                  <AuthRequired>
                    <DomainsPage />
                  </AuthRequired>
                ),
              },
              {
                path: 'apps',
                element: (
                  <AuthRequired>
                    <AppsPage />
                  </AuthRequired>
                ),
              },
            ],
          },
          {
            path: 'api-usage',
            element: (
              <AuthRequired>
                <Layout>
                  <ApiUsagePage />
                </Layout>
              </AuthRequired>
            ),
          },
        ],
      },
      {
        path: '/login',
        element: (
          <Titled title="Login">
            <Login />
          </Titled>
        ),
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

datadogActions.trackDatadogAction(DATADOG_EVENT.routeChange, {
  pathname: window.location.pathname,
  search: window.location.search,
});

router.subscribe((state) => {
  datadogActions.trackDatadogAction(DATADOG_EVENT.routeChange, {
    pathname: state.location.pathname,
    search: state.location.search,
  });
});

const App: FC = () => (
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);

export default App;
